import { FocusableElement } from '@react-types/shared';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTooltipTrigger } from 'react-aria';
import { usePopper } from 'react-popper';
import { useTooltipTriggerState } from 'react-stately';
import { TooltipProvider } from '../TooltipContext';
import { TooltipContextValue } from '../TooltipContext/types';
import { TooltipContainerProps } from './types';

function TooltipContainer<
  TTooltip extends HTMLElement = HTMLElement,
  TArrow extends HTMLElement = HTMLElement,
>({
  arrow,
  children,
  contents,
  defaultOpen,
  delay,
  isDisabled,
  isOpen,
  offset,
  onOpenChange,
  placement,
  strategy,
  trigger,
}: TooltipContainerProps<TTooltip, TArrow>) {
  const targetRef = useRef<FocusableElement | null>(null);
  const [targetEl, setTargetEl] = useState<FocusableElement | null>(null);

  const popperRef = useRef<TTooltip | null>(null);
  const [popperEl, setPopperEl] = useState<HTMLElement | null>(null);
  const [arrowEl, setArrowEl] = useState<TArrow | null>(null);

  const state = useTooltipTriggerState({
    defaultOpen,
    delay,
    isDisabled,
    isOpen,
    onOpenChange,
    trigger,
  });

  const { attributes, styles } = usePopper(targetEl, popperEl, {
    modifiers: [
      { name: 'offset', options: { offset: [0, offset] } },
      { name: 'arrow', options: { element: arrowEl } },
    ],
    placement,
    strategy,
  });

  const { triggerProps, tooltipProps } = useTooltipTrigger(
    {
      defaultOpen,
      delay,
      isDisabled,
      isOpen,
      onOpenChange,
      trigger,
    },
    state,
    targetRef,
  );

  // react-aria wants a mutable ref object
  // popperjs wants an actual HTMLElement
  const handleTargetRef = useCallback((el: FocusableElement | null) => {
    targetRef.current = el;
    setTargetEl(el);
  }, []);

  // react-aria wants a mutable ref object
  // popperjs wants an actual HTMLElement
  const handlePopperRef = useCallback((el: TTooltip) => {
    popperRef.current = el;
    setPopperEl(el);
  }, []);

  const contextValue: TooltipContextValue<TTooltip, TArrow> = useMemo(
    () => ({
      arrow,
      state,
      tooltipProps,
      arrowRef: setArrowEl,
      attributes: attributes.popper,
      arrowStyle: styles.arrow,
      tooltipStyle: styles.popper,
      tooltipRef: handlePopperRef,
    }),
    [
      arrow,
      attributes.popper,
      handlePopperRef,
      state,
      styles.arrow,
      styles.popper,
      tooltipProps,
    ],
  );

  return (
    <span>
      {React.isValidElement(children) &&
        React.cloneElement(children, {
          ref: handleTargetRef,
          ...triggerProps,
        })}
      <TooltipProvider value={contextValue}>
        {typeof contents === 'function' ? contents(contextValue) : contents}
      </TooltipProvider>
    </span>
  );
}

export default TooltipContainer;
